import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const RATES = ["0", "7", "10", "14", "20"];
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const str = (n) => (Number.isFinite(n) ? String(r2(n)) : "");

// Iter-320 — lines + totals of a retro invoice from a form value (edit / OCR draft).
export const retroLinesFrom = (src) => {
  const lines = (src?.lines || []).map((l) => ({ description: l.description || "", amount_ht: l.total_ht != null ? String(l.total_ht) : "" }));
  const ht = Number(src?.total_ht);
  const tva = Number(src?.total_tva);
  let rate = src?.vat_rate != null ? Math.round(Number(src.vat_rate) * (Number(src.vat_rate) <= 1 ? 100 : 1)) : (ht > 0 && Number.isFinite(tva) ? Math.round((tva / ht) * 100) : 20);
  if (!RATES.includes(String(rate))) rate = 20;
  return {
    lines: lines.length ? lines : [{ description: src?.description || "", amount_ht: src?.total_ht != null ? String(src.total_ht) : "" }],
    vat_rate: String(rate),
  };
};

// Totals recomputed from HT lines and the single VAT rate.
const fromHt = (lines, rate) => {
  const ht = r2(lines.reduce((s, l) => s + (Number(l.amount_ht) || 0), 0));
  const tva = r2(ht * Number(rate) / 100);
  return { total_ht: str(ht), total_tva: str(tva), total_ttc: str(ht + tva) };
};

export default function RetroInvoiceAmounts({ form, set }) {
  const multi = form.lines.length > 1;
  const setLines = (lines) => set({ lines, ...fromHt(lines, form.vat_rate) });
  const setLine = (i, patch) => setLines(form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const onTtc = (v) => {
    if (v === "") { set({ total_ttc: "", total_ht: "", total_tva: "", lines: [{ ...form.lines[0], amount_ht: "" }] }); return; }
    const ht = r2((Number(v) || 0) / (1 + Number(form.vat_rate) / 100));
    set({ total_ttc: v, total_ht: str(ht), total_tva: str(r2((Number(v) || 0) - ht)), lines: [{ ...form.lines[0], amount_ht: str(ht) }] });
  };
  const onTva = (v) => set({ total_tva: v, total_ttc: str((Number(form.total_ht) || 0) + (Number(v) || 0)) });
  return (
    <div className="space-y-2" data-testid="retro-amounts">
      <div className="flex items-center justify-between">
        <Label>Lignes de facturation</Label>
        <label className="inline-flex items-center gap-2 text-xs text-zinc-600">
          TVA
          <select value={form.vat_rate} onChange={(e) => set({ vat_rate: e.target.value, ...fromHt(form.lines, e.target.value) })}
            className="h-8 px-2 border border-zinc-200 rounded-md bg-white text-sm" data-testid="retro-vat-rate">
            {RATES.map((r) => <option key={r} value={r}>{r} %</option>)}
          </select>
        </label>
      </div>
      {form.lines.map((l, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`retro-line-${i}`}>
          <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder={i === 0 ? "Prestation facturée" : `Ligne ${i + 1}`}
            className="flex-1" data-testid={i === 0 ? "retro-description" : `retro-line-desc-${i}`} />
          <Input type="number" step="0.01" value={l.amount_ht} onChange={(e) => setLine(i, { amount_ht: e.target.value })} placeholder="HT"
            className="w-32 text-right" data-testid={`retro-line-ht-${i}`} />
          <button type="button" disabled={form.lines.length === 1} onClick={() => setLines(form.lines.filter((_, j) => j !== i))}
            title="Supprimer la ligne" className="h-9 w-9 inline-flex items-center justify-center rounded border border-zinc-200 text-zinc-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
            data-testid={`retro-line-remove-${i}`}>
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setLines([...form.lines, { description: "", amount_ht: "" }])}
        className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-zinc-950" data-testid="retro-line-add">
        <Plus size={12} /> Ajouter une ligne
      </button>
      <div className="grid grid-cols-3 gap-3 pt-1">
        <div>
          <Label>Montant HT</Label>
          <Input type="number" step="0.01" value={form.total_ht} readOnly={multi}
            onChange={(e) => set({ ...(e.target.value === "" ? { total_tva: "", total_ttc: "" } : fromHt([{ amount_ht: e.target.value }], form.vat_rate)), total_ht: e.target.value, lines: [{ ...form.lines[0], amount_ht: e.target.value }] })}
            className={multi ? "bg-zinc-50" : ""} data-testid="retro-ht" />
        </div>
        <div>
          <Label>TVA ({form.vat_rate} %)</Label>
          <Input type="number" step="0.01" value={form.total_tva} onChange={(e) => onTva(e.target.value)} data-testid="retro-tva" />
        </div>
        <div>
          <Label>Montant TTC <span className="text-red-600">*</span></Label>
          <Input type="number" step="0.01" value={form.total_ttc} readOnly={multi}
            onChange={(e) => onTtc(e.target.value)} className={multi ? "bg-zinc-50" : ""} data-testid="retro-ttc" />
        </div>
      </div>
      <div className="text-[11px] text-zinc-500">Calcul automatique : HT → TVA → TTC, ou TTC → HT selon le taux. {multi ? "Avec plusieurs lignes, les totaux suivent les lignes." : ""}</div>
    </div>
  );
}
